import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { Carousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import { fetchData, getMediaUrl } from '../api/api';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Contacts from '../components/Contacts';
import '../styles/ProjectDetails.css';

const IMAGE_EXTS = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];
const VIDEO_EXTS = ['mp4', 'webm', 'ogg', 'avi', 'mov', 'flv', 'mkv'];

const getMediaType = (url = '') => {
    const ext = url.split('.').pop().toLowerCase().split('?')[0];
    if (IMAGE_EXTS.includes(ext)) return 'image';
    if (VIDEO_EXTS.includes(ext)) return 'video';
    return 'unknown';
};

const statusMap = {
    'completed':   { label: 'Completed',   color: 'var(--cyan)'   },
    'in-progress': { label: 'In Progress', color: 'var(--spark)'  },
    'in progress': { label: 'In Progress', color: 'var(--spark)'  },
    'planned':     { label: 'Planned',     color: 'var(--violet)' },
};

export default function ProjectDetails() {
    const { id }    = useParams();
    const navigate  = useNavigate();
    const [project, setProject] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error,   setError]   = useState(null);
    const heroRef               = useRef(null);

    useEffect(() => {
        (async () => {
            try {
                setLoading(true);
                const data = await fetchData(`/projects/${id}`);
                if (data?.message) {
                    setProject(data.message);
                    document.title = `${data.message.name} — SW.`;
                    setError(null);
                } else {
                    setError('Project not found.');
                }
            } catch (err) {
                console.error(err);
                setError('Failed to load project details.');
            } finally {
                setLoading(false);
            }
        })();
        return () => { document.title = 'Samwel Waithaka'; };
    }, [id]);

    // Fade in hero on load
    useEffect(() => {
        if (project && heroRef.current) {
            setTimeout(() => heroRef.current?.classList.add('pd-hero-visible'), 50);
        }
    }, [project]);

    // Markdown custom renderers
    const mdComponents = {
        a: ({ children, href }) => (
            <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>
        ),
        img: ({ alt, src, title }) => (
            <img src={src} alt={alt} title={title} className="md-img" />
        ),
    };

    const status = project?.status
        ? (statusMap[project.status.toLowerCase()] || { label: project.status, color: 'var(--dim)' })
        : null;

    /* ── ERROR ── */
    if (error && !loading) {
        return (
            <>
                <Header />
                <div className="pd-container pd-error-wrap">
                    <div className="pd-error">
                        <p className="pd-error-code">404</p>
                        <h2>Project not found</h2>
                        <p>{error}</p>
                        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', marginTop: '2rem' }}>
                            <button className="btn btn-color-1" onClick={() => navigate('/projects')}>
                                ← All Projects
                            </button>
                            <button className="btn btn-color-2" onClick={() => navigate('/')}>
                                Home
                            </button>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    /* ── LOADING ── */
    if (loading) {
        return (
            <>
                <Header />
                <div className="pd-container pd-loading-wrap">
                    <div className="loader"><span /><span /><span /></div>
                    <p className="state-msg" style={{ marginTop: '1.5rem' }}>
                        Loading project<span className="cursor-blink" />
                    </p>
                </div>
                <Footer />
            </>
        );
    }

    return (
        <>
            <Header />

            <div className="pd-container">

                {/* ── HERO ── */}
                <div className="pd-hero" ref={heroRef}>
                    {/* Back nav */}
                    <button className="pd-back" onClick={() => navigate('/projects')}>
                        ← Back to Projects
                    </button>

                    {/* Meta row */}
                    <div className="pd-meta-row">
                        {project.link && (
                            <span className="pd-category">
                                {project.link.split('/')[0]}
                            </span>
                        )}
                        {status && (
                            <span className="pd-status" style={{ '--sc': status.color }}>
                                <span className="pd-status-dot" />
                                {status.label}
                            </span>
                        )}
                    </div>

                    <h1 className="pd-title">{project.name}</h1>
                    <p className="pd-desc">{project.description}</p>

                    {/* External link if available */}
                    {project.link && project.link.startsWith('http') && (
                        <a
                            href={project.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-color-1"
                            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', marginTop: '1.5rem' }}
                        >
                            View Live ↗
                        </a>
                    )}
                </div>

                {/* ── MEDIA CAROUSEL ── */}
                {project.media?.length > 0 && (
                    <div className="pd-carousel-wrap">
                        <Carousel
                            showThumbs={false}
                            showStatus={false}
                            dynamicHeight={false}
                            autoPlay={true}
                            interval={8000}
                            infiniteLoop={true}
                            stopOnHover={true}
                            emulateTouch={true}
                            swipeable={true}
                        >
                            {project.media.map((media, i) => {
                                const url  = getMediaUrl(media);
                                const type = getMediaType(url);
                                return (
                                    <div key={i} className="pd-media-slide">
                                        {type === 'video' ? (
                                            <video autoPlay loop muted playsInline className="pd-media-video">
                                                <source src={url} type="video/mp4" />
                                            </video>
                                        ) : (
                                            <img
                                                src={url}
                                                alt={`${project.name} media ${i + 1}`}
                                                className="pd-media-img"
                                                onError={e => { e.target.parentElement.style.display = 'none'; }}
                                            />
                                        )}
                                    </div>
                                );
                            })}
                        </Carousel>
                    </div>
                )}

                {/* ── NOTES / MARKDOWN ── */}
                {project.notes && (
                    <div className="pd-notes">
                        <div className="pd-notes-label">
                            <span className="section__text__p1">Project Notes</span>
                        </div>
                        <ReactMarkdown className="pd-markdown" components={mdComponents}>
                            {project.notes}
                        </ReactMarkdown>
                    </div>
                )}

                {/* ── BOTTOM NAV ── */}
                <div className="pd-bottom-nav">
                    <button className="btn btn-color-2" onClick={() => navigate('/projects')}>
                        ← All Projects
                    </button>
                    <button className="btn btn-color-1" onClick={() => navigate('/')}>
                        Home
                    </button>
                </div>

            </div>

            <Contacts />
            <Footer />
        </>
    );
}